/**
 * @flow
 */

import React, { useState, useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import { Button, Input } from 'semantic-ui-react';
import { uniqBy, property, cloneDeep, find } from 'lodash';

import withModal from '../../../../lib/withModal';
import { Select as BaseSelect } from '../../../../components';
import CreateClientDialog from '../../../clients/containers/CreateClientDialog';
import ClientDetails from '../../../clients/components/details/ClientDetails';
import BlockedClientDialog from '../../../clients/components/BlockedClientDialog';
import { MASTER_CLIENT_SELECT_FRAGMENT } from '../../containers/masterAccessQueries';

export const GET_ALL_CLIENTS_QUERY = gql`
    query getClients(
        $salon: ID!
        $client: ID!
        $withClient: Boolean!
        $search: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    clients(search: $search, first: 30) {
                        nodes {
                            id
                            ...client
                        }
                    }
                    client(id: $client) @include(if: $withClient) {
                        id
                        ...client
                    }
                }
            }
            ...onMasterClientSelect
        }
    }

    fragment client on Client {
        email
        firstName
        lastName
        phone
        notes
        birthday
        blockedAt
        blockedBy {
            id
            firstName
            lastName
        }
        gender
        type
        deletedAt
    }

    ${MASTER_CLIENT_SELECT_FRAGMENT}
`;

const intlMessages = defineMessages({
    walkinClient: {
        id: 'pages.appointments.details.AppointmentDetails.walkinClient',
        defaultMessage: 'Walk-in Client',
    },
});

const mapOptions = (intl, item) => ({
    text:
        item.firstName !== '' || item.lastName !== ''
            ? `${item.firstName} ${item.lastName}`
            : intl.formatMessage(intlMessages.walkinClient),
    value: item.id,
    description: item.phone,
    'data-notes': item.notes,
});

const Select = styled(BaseSelect)`
    flex: 1 1 auto;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: none !important;
    &::after {
        content: attr(data-phone);
        text-align: right;
        display: inline-block;
        position: absolute;
        right: 35px;
        color: rgba(0, 0, 0, 0.4);
    }
    .item::after {
        content: attr(data-notes);
        display: block;
        color: rgba(0, 0, 0, 0.4);
        margin-top: 10px;
    }
    & > input {
        width: auto !important;
    }
`;

const AddButton = styled(Button)`
    box-shadow: none !important;
    border: 1px solid rgba(34, 36, 38, 0.15) !important;
    border-right-width: 0 !important;
`;

const InfoButtonComponent = styled(Button)`
    &&.disabled {
        box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset !important;
    }
`;

// The button opens modal window with form that creates a new client.
// The trigger button is wrapped in a plain <span> so Semantic's Portal tracks
// a real DOM node for outside-click handling — handing it the styled Button
// directly resolves to a non-DOM ref in this nested/Input context and crashes
// with "node.contains is not a function". (Mirrors ClientDetailsButton.)
const AddButtonTrigger = withModal(
    ({ size, open, onOpen, onClose, onAdd, newAppointmentUseCase }) => (
        <CreateClientDialog
            trigger={
                <span>
                    <AddButton
                        onClick={e => {
                            e.preventDefault();
                            onOpen();
                        }}
                        icon="add"
                        size={size}
                        basic
                    />
                </span>
            }
            open={open}
            onClose={onClose}
            onAdd={onAdd}
            newAppointmentUseCase={newAppointmentUseCase}
        />
    ),
);

const InfoButtonTrigger = withModal(
    ({ size, open, onOpen, onClose, client, onClientUpdate }) => (
        <ClientDetails
            isModalOpen={open}
            client={client}
            onModalClose={onClose}
            onClientUpdate={onClientUpdate}
            trigger={
                <span>
                    <InfoButtonComponent
                        onClick={e => {
                            e.preventDefault();
                            onOpen();
                        }}
                        icon="info"
                        size={size}
                        basic
                        disabled={!client || !client.id}
                    />
                </span>
            }
        />
    ),
);

const ClientSelect = ({
    intl,
    value,
    onChange,
    name,
    change,
    placeholder,
    updateAppointmentClient,
    newAppointmentUseCase,
    onCloseDialog,
    size,
    // Discard the boolean `search` flag callers pass — this component does its
    // own server-side search and renders all returned options.
    search: _search,
    ...rest
}) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const [search, setSearch] = useState(null);
    const [clientState, setClientState] = useState({});
    const [open, setOpen] = useState(false);

    const { loading, data } = useQuery(GET_ALL_CLIENTS_QUERY, {
        variables: {
            salon,
            client: value || '',
            withClient: !!value,
            search: search || undefined,
        },
        fetchPolicy: 'cache-and-network',
    });

    // Keep the last resolved clients/options in refs so they don't blank out
    // during a cache-and-network refetch (loading=true). An empty Dropdown
    // tears down its menu and Semantic's Portal then crashes on a stale ref
    // ("node.contains is not a function"). [React 17 compat — see 03dc77a6]
    const optionsRef = useRef([]);
    const clientsRef = useRef([]);

    if (!loading && data && data.viewer) {
        const { viewer } = data;
        let nextClients = cloneDeep(viewer.salon.clients.nodes);

        if (viewer.salon.client) {
            nextClients.unshift(viewer.salon.client);
        }

        nextClients = uniqBy(nextClients, property('id'));

        const clientFound = nextClients.some(el => el.id === value);
        const isWalkinClient = value && !clientFound;
        if (isWalkinClient) {
            nextClients.unshift({
                deletedAt: null,
                email: '',
                firstName: '',
                gender: 'MALE',
                id: value,
                lastName: '',
                notes: null,
                birthday: null,
                blockedAt: null,
                blockedBy: null,
                phone: '',
                type: 'LOCAL',
                __typename: 'Client',
            });
        }

        clientsRef.current = nextClients;
        optionsRef.current = nextClients.map(mapOptions.bind(null, intl));
    }

    const clients = clientsRef.current;
    const options = optionsRef.current;
    const selectedClient = find(clients, ['id', value]) || {};

    useEffect(() => {
        const selected = find(clients, ['id', value]) || {};
        if (clientState.id != selected.id) {
            setClientState(selected);
            if (selected.blockedAt) {
                setOpen(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clients, value]);

    const onSearch = (event, dataArg) => setSearch(dataArg.searchQuery);

    // Automatically select a newly created client in the appointment form.
    // shouldDirty is required: RHF setValue defaults to pristine, which
    // would leave the Save button disabled (redux-form change() dirtied).
    const onAdd = client => {
        change(name, client.id, { shouldDirty: true });
        change('notes', client.notes, { shouldDirty: true });
    };

    const onClientUpdate = client => {
        change('notes', client.notes, { shouldDirty: true });
        if (updateAppointmentClient) {
            updateAppointmentClient(client);
        }
    };

    const handleChange = (e, dataArg) => {
        const client = find(clients, { id: dataArg.value });
        change('notes', client ? client.notes : '', { shouldDirty: true });
        // On value change the component refetches and rerenders. Without the
        // delay the click lands on the modal backdrop and closes the modal.
        setTimeout(() => onChange(e, dataArg), 0);
    };

    return (
        <Input action>
            <Select
                {...rest}
                onSearchChange={onSearch}
                size={size}
                fluid
                data-phone={selectedClient.phone}
                search={() => options}
                value={value}
                onChange={handleChange}
                name={name}
                placeholder={placeholder}
                options={options}
                loading={loading}
            />
            <AddButtonTrigger
                size={size}
                onAdd={onAdd}
                newAppointmentUseCase={newAppointmentUseCase}
            />
            <InfoButtonTrigger
                size={size}
                client={selectedClient}
                onClientUpdate={onClientUpdate}
            />
            <BlockedClientDialog
                client={selectedClient}
                open={open}
                onCloseDialog={onCloseDialog}
                onClose={() => {
                    setOpen(false);
                }}
            />
        </Input>
    );
};

export default injectIntl(ClientSelect);
